import React, { useState, useEffect } from "react";
import { useInView } from "react-intersection-observer";
import { useNavigate } from "react-router-dom";
import "./CSS/Departments.css";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { useI18nField /*, useI18n or useLocale if you have it */ } from "../locale/LocaleContext";

const DepartmentItem = ({ dept }) => {
  const navigate = useNavigate();
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const f = useI18nField();

  const handleDeptClick = () => navigate(`/departments/${dept.id}`);

  const name = f(dept, "name"); // string in the active locale
  return (
    <div
      ref={ref}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(50px)",
        transition: "opacity 0.6s ease-out, transform 0.6s ease-out",
        border: "1px solid #D3D3D3",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
        padding: "0.5rem",
        cursor: "pointer",
        fontFamily: "Economica",
      }}
      className="card department bg-white rounded-lg text-center flex flex-col justify-start items-center"
      onClick={handleDeptClick}
    >
      <img
        src={dept.imageUrl}
        alt={name}
        style={{ maxWidth: "100%", height: "auto" }}
        className="rounded-lg"
      />
      <h3>{name}</h3>
    </div>
  );
};

const Departments = () => {
  const [departmentsData, setDepartmentsData] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const f = useI18nField();
  // If you have a hook that exposes the current language, use it:
  // const { lang } = useI18n(); // or useLocale()

  // Simple helper to translate inline UI strings via `f` without adding a separate dictionary:
  const tr = (en, fr) => f({ name: { en, fr } }, "name");

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const snapshot = await getDocs(collection(db, "departments"));
        const data = snapshot.docs.map((doc) => {
          const d = { id: doc.id, ...doc.data() };

          // Normalize `name` to {en, fr} if it's a plain string
          const name =
            typeof d.name === "string" ? { en: d.name, fr: d.name } : d.name;

          // Normalize subdepartments similarly
          const subdepartments = Array.isArray(d.subdepartments)
            ? d.subdepartments.map((s) => ({
                ...s,
                name:
                  typeof s.name === "string"
                    ? { en: s.name, fr: s.name }
                    : s.name,
              }))
            : [];

          return { ...d, name, subdepartments };
        });

        setDepartmentsData(data);
      } catch (err) {
        setError(err.message ?? String(err));
      } finally {
        setLoading(false);
      }
    };
    fetchDepartments();
  }, []);

  const handleSelectDepartment = (department) => setSelectedDepartment(department);

  if (loading) return <div>{tr("Loading…", "Chargement…")}</div>;
  if (error) return <div>{tr("Error:", "Erreur :")} {error}</div>;

  return (
    <section
      id="departments"
      className="py-20 px-4 md:px-10 lg:px-16"
      style={{ fontFamily: "Economica", paddingTop: "30px" }}
    >
      {/* Translated section title */}
      <h1 className="text-3xl text-start mb-8">
        {tr("Departments", "Départements")}
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {departmentsData.map((dept) => (
          <div key={dept.id} onClick={() => handleSelectDepartment(dept)}>
            <DepartmentItem dept={dept} />
          </div>
        ))}
      </div>

      {selectedDepartment && (
        <div className="mt-8">
          <h3 className="text-2xl font-semibold mb-4">
            {f(selectedDepartment, "name")}
          </h3>

          {Array.isArray(selectedDepartment.subdepartments) &&
          selectedDepartment.subdepartments.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {selectedDepartment.subdepartments.map((subdept) => {
                const subName = f(subdept, "name");
                return (
                  <div
                    key={subdept.id}
                    className="subdept card bg-white rounded-lg p-4 shadow-lg text-center"
                  >
                    <h4 className="font-medium mb-2">{subName}</h4>
                    <img
                      src={subdept.imageUrl}
                      alt={subName}
                      style={{ width: "100px", height: "100px", objectFit: "cover" }}
                    />
                    <button
                      className="mt-2 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                      onClick={() => navigate(`/subdepartments/${subdept.id}`)}
                    >
                      {tr("Explore", "Explorer")} {subName}
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <div>{tr("No subdepartments available.", "Aucun sous-département disponible.")}</div>
          )}
        </div>
      )}
    </section>
  );
};

export default Departments;
